<template>
  <div class="intercom-table-wrapper">
    <table class="intercom-table">
      <thead class="intercom-table-head">
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            :class="getHeaderClass(column)"
            :style="getColumnStyle(column)"
            @click="handleHeaderClick(column)"
          >
            <span class="intercom-table-header-content">
              {{ column.title }}
              <span v-if="column.sortable" class="intercom-table-sort-icon">
                <svg
                  v-if="sortBy === column.sortKey"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  :class="{ 'sort-desc': sortOrder === 'desc' }"
                >
                  <path d="M12 5v14M5 12l7-7 7 7" />
                </svg>
                <svg
                  v-else
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="sort-inactive"
                >
                  <path d="M7 10l5-5 5 5M7 14l5 5 5-5" />
                </svg>
              </span>
            </span>
          </th>
        </tr>
      </thead>
      <tbody class="intercom-table-body">
        <tr
          v-for="(row, index) in data"
          :key="getRowKey(row, index)"
          :class="getRowClass(row, index)"
          @click="handleRowClick(row)"
        >
          <td
            v-for="column in columns"
            :key="column.key"
            :class="getCellClass(column)"
            :style="getColumnStyle(column)"
          >
            <slot
              :name="`cell-${column.key}`"
              :row="row"
              :column="column"
              :value="row[column.key]"
            >
              {{ row[column.key] }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="data.length === 0 && !loading" class="intercom-table-empty">
      <slot name="empty">
        <p>No data available</p>
      </slot>
    </div>

    <div v-if="loading" class="intercom-table-loading">
      <slot name="loading">
        <svg
          class="intercom-table-spinner"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" opacity="0.25" />
          <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75" stroke-linecap="round" />
        </svg>
        <p>Loading...</p>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  /**
   * Table columns
   * @example [{ key: 'name', title: 'Name', width: '200px', align: 'left', sortable: true, sortKey: 'name' }]
   */
  columns: {
    type: Array,
    required: true,
    validator: (value) => {
      return value.every((col) => col.key && col.title)
    },
  },
  /**
   * Table data
   */
  data: {
    type: Array,
    default: () => [],
  },
  /**
   * Row key field name (for unique keys)
   */
  rowKey: {
    type: String,
    default: 'id',
  },
  /**
   * Enable row hover effect
   */
  hoverable: {
    type: Boolean,
    default: true,
  },
  /**
   * Enable row click
   */
  clickable: {
    type: Boolean,
    default: false,
  },
  /**
   * Loading state
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Current sort field
   */
  sortBy: {
    type: String,
    default: '',
  },
  /**
   * Current sort order ('asc' or 'desc')
   */
  sortOrder: {
    type: String,
    default: 'asc',
  },
  /**
   * Index of the keyboard-selected row (-1 = none).
   * Used by usePageShortcuts for virtual focus highlight.
   */
  selectedIndex: {
    type: Number,
    default: -1,
  },
})

const emit = defineEmits(['row-click', 'sort'])

const getRowKey = (row, index) => {
  return row[props.rowKey] || index
}

const getRowClass = (row, index) => {
  return {
    'intercom-table-row--hoverable': props.hoverable,
    'intercom-table-row--clickable': props.clickable,
    'intercom-table-row--selected': props.selectedIndex >= 0 && index === props.selectedIndex,
  }
}

const getHeaderClass = (column) => {
  return {
    [`intercom-table-header--${column.align || 'left'}`]: true,
    'intercom-table-header--sortable': column.sortable,
    'intercom-table-header--sorted': props.sortBy === column.sortKey,
  }
}

const handleHeaderClick = (column) => {
  if (!column.sortable) return

  let newOrder = 'asc'
  if (props.sortBy === column.sortKey) {
    // Toggle order if clicking same column
    newOrder = props.sortOrder === 'asc' ? 'desc' : 'asc'
  }

  emit('sort', { sortBy: column.sortKey, sortOrder: newOrder })
}

const getCellClass = (column) => {
  return {
    [`intercom-table-cell--${column.align || 'left'}`]: true,
  }
}

const getColumnStyle = (column) => {
  const style = {}
  if (column.width) {
    style.width = column.width
  }
  return style
}

const handleRowClick = (row) => {
  if (props.clickable) {
    emit('row-click', row)
  }
}
</script>

<style scoped>
/* ========================================
   INTERCOM TABLE BASE
   ======================================== */
.intercom-table-wrapper {
  position: relative;
  overflow-x: auto;
}

.intercom-table {
  width: 100%;
  border-collapse: collapse;
}

/* ========================================
   TABLE HEAD
   ======================================== */
.intercom-table-head {
  background: var(--color-gray-50);
}

.intercom-table-head th {
  padding: var(--space-3) var(--space-6);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
}

.intercom-table-header--left {
  text-align: left;
}

.intercom-table-header--center {
  text-align: center;
}

.intercom-table-header--right {
  text-align: right;
}

.intercom-table-header--sortable {
  cursor: pointer;
  user-select: none;
}

.intercom-table-header--sortable:hover {
  color: var(--color-gray-700);
}

.intercom-table-header--sorted {
  color: var(--color-blue-600);
}

.intercom-table-header-content {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
}

.intercom-table-sort-icon {
  display: inline-flex;
  align-items: center;
}

.intercom-table-sort-icon svg {
  transition: transform var(--transition-fast);
}

.intercom-table-sort-icon .sort-desc {
  transform: rotate(180deg);
}

.intercom-table-sort-icon .sort-inactive {
  opacity: 0.3;
}

/* ========================================
   TABLE BODY
   ======================================== */
.intercom-table-body tr {
  transition: background var(--transition-fast);
}

.intercom-table-body td {
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--color-gray-200);
  font-size: var(--font-size-base);
  color: var(--color-gray-700);
}

.intercom-table-cell--left {
  text-align: left;
}

.intercom-table-cell--center {
  text-align: center;
}

.intercom-table-cell--right {
  text-align: right;
}

/* ========================================
   ROW STATES
   ======================================== */
.intercom-table-row--hoverable:hover {
  background: var(--color-blue-light);
}

.intercom-table-row--clickable {
  cursor: pointer;
}

.intercom-table-row--selected {
  background: var(--color-blue-light);
  outline: 2px solid var(--color-primary);
  outline-offset: -2px;
}

/* ========================================
   EMPTY & LOADING STATES
   ======================================== */
.intercom-table-empty,
.intercom-table-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-16);
  color: var(--color-gray-500);
}

.intercom-table-spinner {
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-4);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
